import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ReactNodeDialog } from "../utils/dialogUtlities";
import DefaultButton from "./DefaultButton";
import { Col, Row } from "./Grid";
import TextInput from "./TextInput";
import Checkbox from "./Checkbox";
import Select from "./Select";
import Card from "./Card";
import { dynamicFilter, dynamicSort } from "../utils/tableUtilities";
import { distinct, groupBy } from "../utils/arrayUtilities";

const FieldSelectDialog = ({ showModal, columns, additionalColumns = [], selectedFields, onSave, onClose }) => {
    const { t } = useTranslation();
    const [columnFilter, setColumnFilter] = useState();
    const [selected, setSelected] = useState({});

    useEffect(() => setSelected({ ...selectedFields }), [selectedFields]);

    const allColumns = useMemo(() => [...columns, ...additionalColumns], [columns, additionalColumns]);
    const groups = useMemo(() => distinct(allColumns.filter(c => c.csvGroups?.length > 0).flatMap(c => c.csvGroups)), [allColumns])

    const handleGroupSelect = (selectedGroup) => {
        setSelected(allColumns.filter(c => c.csvGroups?.includes(selectedGroup)).reduce((selected, field) => { selected[field.selector] = true; return selected }, {}));
    };

    const toggleSelectedField = selector => setSelected(selected => ({ ...selected, [selector]: !selected[selector] }));

    const filteredColumns = useMemo(() => {
        return dynamicFilter(columns.concat(additionalColumns ?? []).filter(f => f.field), columnFilter).map(f => {
            let placeholder = f.title;
            if (!f.title && typeof f.field === 'string') {
                placeholder = f.field;
            }
            return { ...f, placeholder }
        }).filter(f => f.placeholder)
    }, [columns, additionalColumns, columnFilter]);
    filteredColumns.sort(dynamicSort(['section', 'title']));
    
    const sectionMap = groupBy(filteredColumns.filter(col => col.section), 'section');
    Object.values(sectionMap).forEach(section => section.sort(dynamicSort(['title'])));
    const sections = Object.keys(sectionMap);
    sections.sort((a, b) => a.localeCompare(b));

    const footerControls = <div className="d-flex flex-row">
        <DefaultButton kind="clear" onClick={() => setSelected(columns.reduce((obj, col) => { obj[col.selector] = true; return obj }, {}))}></DefaultButton>
    </div>

    return <ReactNodeDialog showModal={showModal} title={t('selectFieldsTitle')} onSave={() => onSave(selected)} onClose={onClose} 
        size="lg" style={{ overflow: null, display: 'flex', flexDirection: 'column' }} footerControls={footerControls}>
            <Row>
                <Col><TextInput value={columnFilter} live={true} onChange={setColumnFilter} style={{ position: 'sticky', top: 0 }} hideLabel={true} placeholder={t('filter')}
                    prependCtrl={<DefaultButton kind="filter" disabled hideLabel={true} onClick={() => {}}></DefaultButton>}></TextInput></Col>
                {groups?.length > 0 && <Col><Select hideLabel placeholder={t('selectReport')} options={groups} onChange={handleGroupSelect}></Select></Col> }
            </Row>
            <div className="w-100 mt-2" style={{ flex: 1, overflowY: 'auto' }}>
                <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(225px, 1fr))', gap: '8px'}}>{filteredColumns.filter(col => !col.section).map(c => <Checkbox key={`${c.label}_${c.selector}`} placeholder={c.title ?? c.field} checked={!!selected[c.selector]} onChange={() => toggleSelectedField(c.selector)}></Checkbox>)}</div>
                {sections.map(section => <Card key={section} title={section} bodyStyle={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(225px, 1fr))', gap: '8px'}}>
                    {sectionMap[section].map(c => <Checkbox key={`${c.label}_${c.selector}`} placeholder={c.placeholder} checked={!!selected[c.selector]} onChange={() => toggleSelectedField(c.selector)}></Checkbox>)}
               </Card>)}
            </div>
    </ReactNodeDialog>
}

export default FieldSelectDialog